// ── SpreadTotalSection — canonical SpreadsTotals EventRows in GameDetail
//
// Drops the value-dashboard's `EventRows` into GameDetail so the action
// rows + line picker + inline ladders behave identically across the
// SpreadsTotals page and this single-game surface. The MatchupRow is
// suppressed via `hideMatchupRow` — GameDetail's page header already
// shows the matchup + date, so a repeated "Away @ Home" row is noise.
//
// Section-local toolbar in the header controls marketSide + priceMode
// for this card only (independent from any dashboard-level toolbar).
// Defaults: Yes/No = NO, price mode = user's displaySettings default.
//
// Trade-click → forwards through to GameDetail's right-column trade
// ticket via onPillClick.

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWorkingView, useWorkingViewField } from '@/hooks/useWorkingView';
import { WorkingViewReset } from '@/components/atoms/WorkingViewReset';
import { spreadTotalReading } from './readingView';
import { useAccountStore } from '../../stores/accountStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useMarketStore } from '../../stores/marketStore';
import { useUserKalshiPOS } from '../../hooks/useUserKalshiPOS';
import { CompactLinesTable, closestKalshiYesTo50 } from './CompactLinesTable';
import { GameRows } from '../ValueDashboard/GameTable';
import {
  LIQ_HEADER_TOOLTIP_KALSHI,
  LIQ_HEADER_TOOLTIP_POLY,
  VOL_HEADER_TOOLTIP_KALSHI,
  VOL_HEADER_TOOLTIP_POLY,
} from '../ValueDashboard/sharedBookCue';
import { Th } from '../../components/atoms/TableCells';
import { SectionToolbar } from './SectionToolbar';
import { buildSpreadBetRows, buildTotalBetRows } from './lineBetRows';
import { buildBetLineContext } from '../SpreadsTotals/buildBetLineContext';
import {
  linePriceCents,
  resolveLineIndexByKey,
  spreadLineKey,
  synthesizeFromSpread,
  synthesizeFromTotal,
  totalLineKey,
} from '../SpreadsTotals/synthesize';
import type {
  GameEventRow,
  GameRow,
  SpreadLine,
  TeamSide,
  TotalLine,
  TradeTicketContext,
} from '../ValueDashboard/types';
import { lt2CardId, type PillTradeMeta } from '../SpreadsTotals/SpreadsTotalsTable';
import { tryOneClickFromTicketCtx } from '../ValueDashboard/oneClickIntercept';
import { useOneClickStore } from '../../stores/oneClickStore';
import { useLt2Access } from '../../hooks/useLt2Access';
import { useLt2FocusStore } from '../../stores/lt2FocusStore';
import { filterEventExecutableLines } from '../SpreadsTotals/synthesize';
import type { BetLineContext } from '../ValueDashboard/GameDetailPanel';

/** Bundle the parent uses to drive the right-side trade panel's
 *  spread/total mode — same shape SpreadsTotals on the value dashboard
 *  emits via onActiveGameChange + onActiveBetLineContextChange, just
 *  combined for the single consumer here. */
export interface ActiveSpreadTotal {
  game: GameRow;
  context: BetLineContext;
}

interface Props {
  game: GameRow;
  event: GameEventRow;
  /** Optional deep-link hint from the Positions page. If this ticker
   *  matches a spread or total line in the event, that row pre-expands
   *  with the matching line index pre-selected. */
  focusTicker?: string | null;
  focusSide?: 'yes' | 'no';
  onPillClick: (ctx: TradeTicketContext) => void;
  /** Emits the active spread/total bundle (synth game + chip-strip
   *  context) so GameDetail's right-side panel can render its
   *  alternate header with the MiniLineStrip. Null when no row has
   *  been activated yet via a pill click. Fires on every line-index
   *  change too (panel chip selections come back through here). */
  onActiveLineChange?: (active: ActiveSpreadTotal | null) => void;
  /** Controlled activeKind from the parent. Lifted out of local state
   *  so a moneyline pill click at the parent level can force the
   *  section to stop being "active" — preventing the section's emit
   *  effect from clobbering the parent's null with a stale bundle on
   *  the next market-frame-driven event reference churn. */
  activeKind: 'spread' | 'total' | null;
  onActiveKindChange: (kind: 'spread' | 'total' | null) => void;
}

const VIEW_ID_PREFIX = 'game-detail-spread-total';

/** D-020 V2B — stable hotkey-focus scope; this section is always full-game. */
const LT2_GDST_CARD_SCOPE = {
  surface: 'gd-spread-total',
  period: 'full',
  keyPrefix: 'gdst',
} as const;

type SpreadTotalFocusMatch = {
  rowKind: 'spread' | 'total';
  idx: number;
  venue: 'kalshi' | 'polymarket';
};

function normalizeFocusTicker(ticker?: string | null): string | null {
  const trimmed = ticker?.trim();
  return trimmed ? trimmed.toUpperCase() : null;
}

export function findSpreadTotalFocusMatch(
  event: Pick<GameEventRow, 'spreads' | 'totals'>,
  focusTicker?: string | null,
): SpreadTotalFocusMatch | null {
  const target = normalizeFocusTicker(focusTicker);
  if (!target) return null;

  for (let i = 0; i < event.spreads.length; i++) {
    const s = event.spreads[i];
    const kalshi = normalizeFocusTicker(s.kalshi?.marketTicker);
    const poly = normalizeFocusTicker(s.polymarket?.marketTicker);
    if (kalshi === target) {
      return { rowKind: 'spread', idx: i, venue: 'kalshi' };
    }
    if (poly === target) {
      return { rowKind: 'spread', idx: i, venue: 'polymarket' };
    }
  }
  for (let i = 0; i < event.totals.length; i++) {
    const t = event.totals[i];
    const kalshi = normalizeFocusTicker(t.kalshi?.marketTicker);
    const poly = normalizeFocusTicker(t.polymarket?.marketTicker);
    if (kalshi === target) {
      return { rowKind: 'total', idx: i, venue: 'kalshi' };
    }
    if (poly === target) {
      return { rowKind: 'total', idx: i, venue: 'polymarket' };
    }
  }
  return null;
}

export function SpreadTotalSection({
  game,
  event: eventProp,
  focusTicker,
  focusSide,
  onPillClick,
  onActiveLineChange,
  activeKind,
  onActiveKindChange,
}: Props) {
  const defaultPriceMode = useDisplaySettings((s) => s.defaultPriceMode);
  // Odds filter (Dan 2026-08-23): U defaults OFF; thead + GameRows lockstep.
  const showUnbtdOdds = useDisplaySettings((s) => s.showUnbtdOdds);
  const hideOddsColumn = !showUnbtdOdds;
  const spreadTotalViewMode = useDisplaySettings((s) => s.spreadTotalViewMode);
  const setSpreadTotalViewMode = useDisplaySettings((s) => s.setSpreadTotalViewMode);
  const setOrderbookNeeds = useMarketStore((s) => s.setOrderbookNeeds);
  const openOrders = useAccountStore((s) => s.openOrders);

  // D-020 LT2-005: hide Poly-only lines from the execution view under lt2
  // (reveal toggle un-hides; revealed lines stay disabled). Filtered at the
  // top so every downstream read — defaults, focus match, subscriptions,
  // synth builders — sees one consistent world. lt2 off → prop untouched.
  const lt2Access = useLt2Access();
  const showPolyOnlyLines = useDisplaySettings((s) => s.spreadTotalShowPolyOnlyLines);
  const setShowPolyOnlyLines = useDisplaySettings((s) => s.setSpreadTotalShowPolyOnlyLines);
  const hidePolyOnlyLines = lt2Access && !showPolyOnlyLines;
  const event = useMemo(
    () => (hidePolyOnlyLines ? filterEventExecutableLines(eventProp, false) : eventProp),
    [eventProp, hidePolyOnlyLines],
  );

  // Initial focus, when the line is already present on first render.
  // A follow-up effect below keeps retrying for cold hydration, where
  // the GameEventRow can exist before its target spread/total line does.
  const focusMatch = useMemo<SpreadTotalFocusMatch | null>(() => {
    return findSpreadTotalFocusMatch(event, focusTicker);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reading = useWorkingView(spreadTotalReading, JSON.stringify([event.key, focusTicker ?? null, focusSide ?? null]), {
    marketSide: focusSide ?? 'no', priceMode: defaultPriceMode,
  });
  const [marketSide, setMarketSide] = useWorkingViewField(reading, 'marketSide');
  const [priceMode, setPriceMode] = useWorkingViewField(reading, 'priceMode');
  // Default-select closest-to-50¢ line for each market type. Under LT2 the
  // picked market is sticky by identity across live reorder; flag-off keeps
  // the legacy index behavior. When
  // we have a focusMatch, seed the corresponding index from it instead.
  const defaultSpreadIdx = useMemo(
    () => closestKalshiYesTo50(event.spreads.map(linePriceCents)),
    [event.spreads],
  );
  const defaultTotalIdx = useMemo(
    () => closestKalshiYesTo50(event.totals.map(linePriceCents)),
    [event.totals],
  );
  const [spreadIdx, setSpreadIdx] = useState<number | null>(
    focusMatch?.rowKind === 'spread' ? focusMatch.idx : null,
  );
  const [totalIdx, setTotalIdx] = useState<number | null>(
    focusMatch?.rowKind === 'total' ? focusMatch.idx : null,
  );
  const [spreadKey, setSpreadKey] = useState<string | null>(() => {
    const line = focusMatch?.rowKind === 'spread' ? event.spreads[focusMatch.idx] : null;
    return line ? spreadLineKey(line) : null;
  });
  const [totalKey, setTotalKey] = useState<string | null>(() => {
    const line = focusMatch?.rowKind === 'total' ? event.totals[focusMatch.idx] : null;
    return line ? totalLineKey(line) : null;
  });
  const [expandedRow, setExpandedRow] = useState<'spread' | 'total' | null>(
    focusMatch?.rowKind ?? null,
  );
  // Per-row expand state for the Detailed (GameRows) view — keyed by the synth
  // row's key + team side. Independent of the compact EventRows' 'spread'|'total'
  // expandedRow above, so switching views doesn't cross-drive an expansion.
  const [detailExpanded, setDetailExpanded] = useState<{ key: string; side: TeamSide } | null>(null);
  // #608 — see the twin in SpreadsTotals/index.tsx. The compact card keeps
  // its id while a vanished selection falls back to a visible default line,
  // so a held hotkey focus would otherwise point at a market the trader never
  // chose. Layout effect, and equality-guarded inside the store, so it can
  // never disturb another card's focus.
  useLayoutEffect(() => {
    const { clearFocusIf } = useLt2FocusStore.getState();
    if (spreadKey != null && !event.spreads.some((l) => spreadLineKey(l) === spreadKey)) {
      clearFocusIf(lt2CardId(LT2_GDST_CARD_SCOPE.keyPrefix, event.key, 'spread'));
    }
    if (totalKey != null && !event.totals.some((l) => totalLineKey(l) === totalKey)) {
      clearFocusIf(lt2CardId(LT2_GDST_CARD_SCOPE.keyPrefix, event.key, 'total'));
    }
  }, [event, spreadKey, totalKey]);

  // Identity-resolved: the Poly-only filter can shorten or reorder arrays
  // under a sticky user pick. lt2-gated (review finding, 2026-08-18): with the flag
  // off there is no filter, and V1 rendered the no-line state on a feed
  // shrink rather than silently selecting a different line.
  const activeSpreadIdx = lt2Access
    ? resolveLineIndexByKey(event.spreads, spreadKey, spreadLineKey, defaultSpreadIdx)
    : (spreadIdx ?? defaultSpreadIdx);
  const activeTotalIdx = lt2Access
    ? resolveLineIndexByKey(event.totals, totalKey, totalLineKey, defaultTotalIdx)
    : (totalIdx ?? defaultTotalIdx);
  // activeKind is parent-controlled — see Props above. Local alias keeps
  // the rest of the component readable.
  const setActiveKind = onActiveKindChange;
  const appliedFocusRef = useRef<string | null>(null);
  useEffect(() => {
    const target = normalizeFocusTicker(focusTicker);
    if (!target) {
      appliedFocusRef.current = null;
      return;
    }
    const applyKey = `${target}:${focusSide ?? ''}`;
    if (appliedFocusRef.current === applyKey) return;
    const match = findSpreadTotalFocusMatch(event, target);
    if (!match) return;

    appliedFocusRef.current = applyKey;
    if (focusSide) setMarketSide(focusSide);
    if (match.rowKind === 'spread') {
      const line = event.spreads[match.idx];
      if (!line) return;
      setSpreadIdx(match.idx);
      setSpreadKey(spreadLineKey(line));
      setExpandedRow(match.rowKind);
      setActiveKind(match.rowKind);
      onPillClick({
        game: synthesizeFromSpread(event, line),
        teamSide: 'away',
        venue: match.venue,
        initialOrderSide: focusSide ?? marketSide,
        postOnly: priceMode === 'maker',
      });
    } else {
      const line = event.totals[match.idx];
      if (!line) return;
      setTotalIdx(match.idx);
      setTotalKey(totalLineKey(line));
      setExpandedRow(match.rowKind);
      setActiveKind(match.rowKind);
      onPillClick({
        game: synthesizeFromTotal(event, line),
        teamSide: 'away',
        venue: match.venue,
        initialOrderSide: focusSide ?? marketSide,
        postOnly: priceMode === 'maker',
      });
    }
  }, [event, focusTicker, focusSide, marketSide, onPillClick, priceMode, setActiveKind, setMarketSide]);

  // Subscribe to depth for every Kalshi spread/total ticker on this
  // event so the inline ladder paints immediately on first click. Poly
  // tickers go through the same channel.
  const allTickers = useMemo<string[]>(() => {
    const out: string[] = [];
    for (const sp of event.spreads) {
      if (sp.kalshi?.marketTicker) out.push(sp.kalshi.marketTicker);
      if (sp.polymarket?.marketTicker) out.push(sp.polymarket.marketTicker);
    }
    for (const t of event.totals) {
      if (t.kalshi?.marketTicker) out.push(t.kalshi.marketTicker);
      if (t.polymarket?.marketTicker) out.push(t.polymarket.marketTicker);
    }
    return out;
  }, [event]);
  useEffect(() => {
    const viewId = `${VIEW_ID_PREFIX}:${event.key}`;
    setOrderbookNeeds(viewId, allTickers);
    return () => {
      setOrderbookNeeds(viewId, []);
    };
  }, [event.key, allTickers, setOrderbookNeeds]);

  // Position/order slices for the same set of Kalshi tickers — drives
  // the strip's C/O rings via EventRows' stripAggregates calc.
  const kalshiTickers = useMemo<string[]>(() => {
    const out: string[] = [];
    for (const sp of event.spreads) if (sp.kalshi?.marketTicker) out.push(sp.kalshi.marketTicker);
    for (const t of event.totals) if (t.kalshi?.marketTicker) out.push(t.kalshi.marketTicker);
    return out;
  }, [event]);
  // #666: these sections render HUMAN order chips only, so they read the
  // display lane (bounded per-order retention with provenance) exactly like
  // the Value Dashboard's humanOrdersByTicker. Strict fail-closed consumers
  // (alerts/automation) live elsewhere and keep the strict lane.
  const { positionByTicker, ordersByTicker: strictOrdersByTicker, displayOrdersByTicker } =
    useUserKalshiPOS(kalshiTickers);
  const ordersByTicker = displayOrdersByTicker ?? strictOrdersByTicker;

  // Detailed-view rows: the same synth GameRows the Game Lines detailed table
  // builds from this period's spread/total lines (shared lineBetRows helper).
  const detailRows = useMemo(
    () => [...buildSpreadBetRows(event), ...buildTotalBetRows(event)],
    [event],
  );

  const handlePillTrade = (
    rowKind: 'spread' | 'total',
    venue: 'kalshi' | 'polymarket',
    side: 'yes' | 'no',
    cents: number,
    meta?: PillTradeMeta,
  ) => {
    setActiveKind(rowKind);
    // A multi-line block click names its OWN line via meta.lineIdx; follow
    // the click so the panel/chips agree with the market just traded.
    const clickedIdx =
      meta?.lineIdx ?? (rowKind === 'spread' ? activeSpreadIdx : activeTotalIdx);
    if (meta?.lineIdx != null) {
      if (rowKind === 'spread') {
        setSpreadIdx(meta.lineIdx);
        const line = event.spreads[meta.lineIdx];
        if (line) setSpreadKey(spreadLineKey(line));
      } else {
        setTotalIdx(meta.lineIdx);
        const line = event.totals[meta.lineIdx];
        if (line) setTotalKey(totalLineKey(line));
      }
    }
    // D-020 interception, at the section level because the ticket ctx below
    // deliberately carries the PARENT game (panel resolves the bet through
    // onActiveLineChange) — the intercept needs the clicked LINE's own synth
    // so identity + guard run against the actual spread/total book.
    const activeLine =
      rowKind === 'spread' ? event.spreads[clickedIdx] : event.totals[clickedIdx];
    if (activeLine) {
      const synth =
        rowKind === 'spread'
          ? synthesizeFromSpread(event, activeLine as SpreadLine)
          : synthesizeFromTotal(event, activeLine as TotalLine);
      if (
        tryOneClickFromTicketCtx({
          ctx: {
            game: synth,
            teamSide: 'away',
            venue,
            prefillPrice: cents,
            initialOrderSide: side,
            postOnly: priceMode === 'maker',
            meta,
          },
          surface: 'gd-spread-total',
          period: 'full',
          priceMode,
        })
      ) {
        return;
      }
    }
    // Leaving the hot path on a genuine priced click disarms (V1 boundary).
    if (useOneClickStore.getState().armed) useOneClickStore.getState().disarm();
    onPillClick({
      game,
      teamSide: 'away',
      venue,
      initialOrderSide: side,
      prefillPrice: cents,
      postOnly: priceMode === 'maker',
    });
    // Flip the section's YES/NO toggle to match the pill — same UX
    // rule the moneyline section uses.
    if (side !== marketSide) setMarketSide(side);
  };

  // Stable callbacks so buildBetLineContext's closures don't churn
  // every render. Keep these outside the effect for the same reason.
  const handlePanelSpreadSelect = useCallback((next: number) => {
    setSpreadIdx(next);
    const line = event.spreads[next];
    if (line) setSpreadKey(spreadLineKey(line));
  }, [event.spreads]);
  const handlePanelTotalSelect = useCallback((next: number) => {
    setTotalIdx(next);
    const line = event.totals[next];
    if (line) setTotalKey(totalLineKey(line));
  }, [event.totals]);

  // Emit the active spread/total bundle whenever the kind or its idx
  // changes. Covers two paths:
  //   • Pill click in this section → setActiveKind → effect fires →
  //     parent learns this section is now the active source.
  //   • Chip click in the panel's MiniLineStrip → onSelectIdx
  //     (wired below) → setSpreadIdx/setTotalIdx → effect fires →
  //     parent + panel resync to the new line.
  //
  // INVARIANT: at most ONE emit per (kind, idx, event-frame). The bundle is
  // freshly allocated each emit, and the parent stores it with setState — so
  // a render-driven re-fire (e.g. an unstable callback prop identity, the
  // exact bug a0453ef introduced and 3c6bca9 fixed) would otherwise loop
  // render→emit→setState→render unboundedly and wedge the main thread.
  // The ref guard makes that whole failure class structurally impossible
  // (no React Compiler here — React 18, manual memoization only) while
  // preserving the per-frame re-emits the order panel needs to tick prices
  // (`event` re-derives with a new identity on every market frame).
  const lastEmitRef = useRef<{
    kind: 'spread' | 'total';
    idx: number;
    event: typeof event;
  } | null>(null);
  useEffect(() => {
    if (!onActiveLineChange) return;
    if (activeKind == null) {
      lastEmitRef.current = null;
      onActiveLineChange(null);
      return;
    }
    const activeIdx = activeKind === 'spread' ? activeSpreadIdx : activeTotalIdx;
    const last = lastEmitRef.current;
    if (last && last.kind === activeKind && last.idx === activeIdx && last.event === event) {
      return; // same selection, same frame — nothing new to tell the parent
    }
    lastEmitRef.current = { kind: activeKind, idx: activeIdx, event };
    if (activeKind === 'spread') {
      const line = event.spreads[activeSpreadIdx];
      if (!line) {
        lastEmitRef.current = null;
        onActiveLineChange(null);
        return;
      }
      onActiveLineChange({
        game: synthesizeFromSpread(event, line),
        context: buildBetLineContext({
          kind: 'spread',
          event,
          lines: event.spreads,
          selectedIdx: activeSpreadIdx,
          onSelectIdx: handlePanelSpreadSelect,
        }),
      });
    } else {
      const line = event.totals[activeTotalIdx];
      if (!line) {
        lastEmitRef.current = null;
        onActiveLineChange(null);
        return;
      }
      onActiveLineChange({
        game: synthesizeFromTotal(event, line),
        context: buildBetLineContext({
          kind: 'total',
          event,
          lines: event.totals,
          selectedIdx: activeTotalIdx,
          onSelectIdx: handlePanelTotalSelect,
        }),
      });
    }
  }, [
    activeKind,
    activeSpreadIdx,
    activeTotalIdx,
    event,
    onActiveLineChange,
    handlePanelSpreadSelect,
    handlePanelTotalSelect,
  ]);

  return (
    <section data-view-anchor="spread-total" className="bg-card border-border rounded-lg border">
      <div className="border-border flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3">
        <h2 className="text-foreground text-lg font-semibold">Spread / Total</h2>
        {/* Same trailing cluster as Game Lines: toolbar, hairline, Reset view. */}
        <div className="flex shrink-0 items-center gap-2">
          <SectionToolbar
            priceMode={priceMode}
            onPriceMode={setPriceMode}
            marketSide={marketSide}
            onMarketSide={setMarketSide}
            viewMode={spreadTotalViewMode}
            onViewMode={setSpreadTotalViewMode}
            showPolyOnlyLines={lt2Access ? showPolyOnlyLines : undefined}
            onShowPolyOnlyLines={lt2Access ? setShowPolyOnlyLines : undefined}
          />
          <span aria-hidden="true" className="bg-border/60 h-4 w-px" />
          <WorkingViewReset
            label="Spread and Total"
            scope="price mode and YES/NO side"
            reset={reading.reset}
            undo={reading.undo}
            canUndo={reading.canUndo}
          />
        </div>
      </div>
      {spreadTotalViewMode === 'detailed' ? (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-spacing-0">
            <thead className="bg-muted/50 border-border border-b">
              <tr>
                <Th className="w-60">Bet</Th>
                {!hideOddsColumn ? (
                  <Th className="w-16 text-center" title="Unabated consensus odds (full-game only) — also hosts your resting orders in the ladder">UNBTD</Th>
                ) : (
                  <Th className="w-10 text-center" title="Your resting orders at each ladder price level">{''}</Th>
                )}
                <Th className="w-32 text-center">Kalshi</Th>
                <Th className="w-16 text-right" title={VOL_HEADER_TOOLTIP_KALSHI}>K Vol</Th>
                <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_KALSHI}>K Liq</Th>
                <Th className="w-16 text-right" title={`Best-bid depth on the active ${marketSide.toUpperCase()} side`}>K Q</Th>
                <Th className="w-16 text-right" title="Kalshi best-bid depth change over the last 1m">K Δ/m</Th>
                <Th className="w-32 text-center">Poly</Th>
                <Th className="w-16 text-right" title={VOL_HEADER_TOOLTIP_POLY}>P Vol</Th>
                <Th className="w-24 text-right" title={LIQ_HEADER_TOOLTIP_POLY}>P Liq</Th>
                <Th className="w-16 text-right" title="Polymarket best-bid depth change over the last 1m">P Δ/m</Th>
              </tr>
            </thead>
            <tbody>
              {detailRows.map((r, i) => (
                <GameRows
                  key={r.synth.key}
                  game={r.synth}
                  gameIndex={i}
                  marketSide={marketSide}
                  priceMode={priceMode}
                  expandedTeamSide={detailExpanded?.key === r.synth.key ? detailExpanded.side : null}
                  onToggleExpand={(key, side) =>
                    setDetailExpanded((e) => (e?.key === key && e?.side === side ? null : { key, side }))
                  }
                  onTradeClick={(_key, side, venue, opts) => {
                    // Detailed rows ARE this period's spread/total lines: rows
                    // [0..spreads.length) are spreads, the rest totals. Select
                    // the matching line so the section's emit effect re-drives
                    // the right-side panel with the correct synth — the parent's
                    // livePanelGame prioritizes activeSpreadTotal.game (from
                    // onActiveLineChange) over the click-time tradeCtx, so
                    // without this a detailed click could open the ticket on a
                    // stale line left active by a prior compact selection.
                    const kind: 'spread' | 'total' = i < event.spreads.length ? 'spread' : 'total';
                    const idx = kind === 'spread' ? i : i - event.spreads.length;
                    if (kind === 'spread') {
                      setSpreadIdx(idx);
                      const line = event.spreads[idx];
                      if (line) setSpreadKey(spreadLineKey(line));
                    } else {
                      setTotalIdx(idx);
                      const line = event.totals[idx];
                      if (line) setTotalKey(totalLineKey(line));
                    }
                    setActiveKind(kind);
                    if (opts?.initialOrderSide) setMarketSide(opts.initialOrderSide);
                    const ctx: TradeTicketContext = {
                      game: r.synth,
                      teamSide: side,
                      venue,
                      prefillPrice: opts?.priceCents,
                      initialOrderSide: opts?.initialOrderSide,
                      // GameRows' ladder derives postOnly itself (bid = join);
                      // no meta here, so the intercept honors this value.
                      postOnly: opts?.postOnly ?? false,
                    };
                    // D-020 interception — the synth ctx here already names
                    // the exact line market, so it feeds the intercept as-is.
                    if (
                      tryOneClickFromTicketCtx({
                        ctx,
                        surface: 'gd-spread-total',
                        period: 'full',
                        priceMode,
                      })
                    ) {
                      return;
                    }
                    if (useOneClickStore.getState().armed && opts?.priceCents != null) {
                      useOneClickStore.getState().disarm();
                    }
                    onPillClick(ctx);
                  }}
                  positionByTicker={positionByTicker}
                  ordersByTicker={ordersByTicker}
                  hideDateColumn
                  hideScoreColumn
                  hideOddsColumn={hideOddsColumn}
                  singleSide={r.singleSide}
                  sideTint
                  labelMaxWidthClass="max-w-[200px]"
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <CompactLinesTable
          event={event}
          marketSide={marketSide}
          priceMode={priceMode}
          positionByTicker={positionByTicker}
          ordersByTicker={ordersByTicker}
          openOrders={openOrders}
          spreadIdx={activeSpreadIdx}
          totalIdx={activeTotalIdx}
          expandedRow={expandedRow}
          onSelectSpreadIdx={(idx) => {
            // Chip-strip scrolls implicitly activate the spread row,
            // matching the value-dashboard behavior: trader touching a chip
            // on the spread row but the panel is showing the total context
            // was confusing. Activate + expand on chip touch so the side
            // panel + emitted bundle both follow the chip.
            setSpreadIdx(idx);
            const line = event.spreads[idx];
            if (line) setSpreadKey(spreadLineKey(line));
            setActiveKind('spread');
            setExpandedRow('spread');
          }}
          onSelectTotalIdx={(idx) => {
            setTotalIdx(idx);
            const line = event.totals[idx];
            if (line) setTotalKey(totalLineKey(line));
            setActiveKind('total');
            setExpandedRow('total');
          }}
          onToggleExpand={(rowKind) =>
            setExpandedRow((prev) => (prev === rowKind ? null : rowKind))
          }
          onPillTrade={handlePillTrade}
          lt2CardScope={LT2_GDST_CARD_SCOPE}
        />
      )}
    </section>
  );
}
